import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { lastValueFrom, take } from 'rxjs';
import { LessorList } from 'src/app/api/models/lessor.model';
import { AppResource } from 'src/app/app.resource';
import { PathType } from 'src/app/core/enums/path-type.enum';
import { NatureResidence, TypeBailleur } from 'src/app/core/models/form-state.model';
import { Stepper } from 'src/app/core/models/stepper.model';
import * as fromInfo from 'src/app/core/state/info';
import { BaseComponent } from 'src/app/shared/components/base-component/base-component.component';
import {
  NUMBER_PIECES_PATH,
  PREFERENTIAL_RATE_PATH,
  ROOT_PATH
} from 'src/app/shared/constants/route.constants';
import { GetMetropoleOrNot } from 'src/app/shared/helpers/metropole-helper.service';
import { HeaderVM } from 'src/app/shared/models/components/headerVm';
import { PopUpInfoVM } from 'src/app/shared/models/components/popupinfoVm';
import { QuestionVM } from 'src/app/shared/models/components/questionVm';
import { LessorCertificateEnum } from 'src/app/shared/models/enum/lessorcertificate.enum';
import { environment } from 'src/environments/environment';
import * as fromAdresse from '../../../core/state/address';
import { AddressState } from '../../../core/state/address';
import { State } from '../../../core/state/core.state';
import * as fromHousing from '../../../core/state/housing';
import * as fromPath from '../../../core/state/path';
import * as fromService from '../../../core/state/service';

interface Lessor {
  text: string;
  value: number | string;
  email: string | null;
  lessorType: number | null;
}

@Component({
  selector: 'app-lessor',
  templateUrl: './lessor.component.html',
  styleUrls: ['./lessor.component.scss']
})
export class LessorComponent extends BaseComponent implements OnInit {
  headerStep!: HeaderVM;
  override question!: QuestionVM;
  // numberLocalInfo!: string;
  clientAccountInfo!: PopUpInfoVM;

  //Form
  social: boolean = false;
  socialForm!: FormGroup;

  private: boolean = false;

  skip!: string;

  filterValue = '';
  selectedLessor!: Lessor | undefined;
  lessorCertificatEnum = LessorCertificateEnum;

  lessorNotFind: boolean = true;
  otherLessor: boolean = false;
  lessorsList: LessorList = [];
  private productCode!: PathType;
  private zipCode!: string;

  placeholderRefClient!: string;
  errorRefClient!: string;

  placeholderCC!: string;
  errorCC!: string;

  emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i;

  get lessor() {
    return this.socialForm.get('lessor');
  }

  get lessorList() {
    return [
      ...(this.lessorsList
        ? this.lessorsList
            .filter(b => this.emailRegex.test(b.email?.trim()))
            .map(b => ({
              text: b.name,
              value: b.id,
              email: b.email,
              lessorType: b.lessorType
            }))
        : []),
      { text: 'Autre', value: 'OTR', email: null, lessorType: null }
    ];
  }

  get brand() {
    return environment.brand;
  }

  lessorCertificateInfo: PopUpInfoVM;

  constructor(
    store: Store<State>,
    resources: AppResource,
    private router: Router,
    private route: ActivatedRoute
  ) {
    super(store, resources);

    this.resource = resources['resource'];
    this.headerStep = new HeaderVM(
      this.resource.header.stepContract,
      3,
      this.resource.header.subStepLessorCertificate,
      4
    );

    this.question = new QuestionVM(this.resource.question.associatedService2);

    this.clientAccountInfo = new PopUpInfoVM([this.resource.infobulle.otherCC]);

    this.skip = this.resource.lessorcertificate.skipStep;
    // Initialize question
    this.lessorCertificateInfo = new PopUpInfoVM([
      this.resource.infobulle.lessorInfo,
      this.resource.infobulle.lessorInfo2
    ]);
  }

  override async ngOnInit() {
    super.ngOnInit();

    this.initForm();
    this.productCode = await lastValueFrom(
      this.store.select(fromPath.selectPathType).pipe(take(1))
    );

    await lastValueFrom(this.store.select(fromAdresse.selectAddress).pipe(take(1))).then(
      (address: AddressState) => {
        if (address.addresseSelection?.AdresseEsh.CodePostal)
          this.zipCode = address.addresseSelection?.AdresseEsh.CodePostal;
        else if (address.addresseManuelle.CodePostal)
          this.zipCode = address.addresseManuelle.CodePostal;
        else if (address.adresseEsh?.CodePostal) this.zipCode = address.adresseEsh?.CodePostal;
      }
    );

    const resolvedLessors: LessorList = this.route.snapshot.data['lessors'] ?? [];
    this.lessorsList = resolvedLessors
      .filter(lessor => lessor.lessorType != 3)
      .sort((a, b) => a.name.localeCompare(b.name));
    await this.loadStateData();

    const realLessors = this.lessorList.filter(l => l.value !== 'OTR');
    if (realLessors.length === 1) {
      this.updateLessor(realLessors[0]);
    }

    this.lessor?.valueChanges.subscribe((value: any) => {
      if (value?.value !== 'OTR') {
        this.lessorNotFind = true;
      }
    });
  }

  private initForm() {
    this.socialForm = new FormGroup(
      {
        lessor: new FormControl<string | null>(null, {
          validators: [Validators.required],
          nonNullable: true
        })
      },
      {
        updateOn: 'change'
      }
    );
  }

  private async loadStateData() {
    let service = await lastValueFrom(this.store.select(fromService.selectService).pipe(take(1)));

    if (service.saisieChoixServices.EnvoiAttestationBailleur?.TypeBailleur == TypeBailleur.Social) {
      this.social = true;

      this.selectedLessor = this.lessorList.find(obj => {
        return (
          obj.text ===
          service.saisieChoixServices.EnvoiAttestationBailleur?.BailleurSocial?.NomBailleur
        );
      });

      if (
        this.selectedLessor == undefined &&
        service.saisieChoixServices.EnvoiAttestationBailleur?.BailleurSocial?.NomBailleur
      ) {
        this.selectedLessor = this.lessorList.find(obj => {
          return obj.value === 'OTR';
        });

        this.updateInfoLessor(
          service.saisieChoixServices.EnvoiAttestationBailleur?.BailleurSocial?.NomBailleur ?? ''
        );
      } else if (this.selectedLessor != undefined) this.updateInfoLessor(this.selectedLessor.text);
    } else if (
      service.saisieChoixServices.EnvoiAttestationBailleur?.TypeBailleur == TypeBailleur.Prive
    ) {
      this.private = true;
    }
  }

  nextStepType(value: string) {
    switch (value) {
      case TypeBailleur.Social:
        this.social = true;
        this.private = false;

        this.store.dispatch(
          fromService.patchService({
            payload: {
              EnvoiAttestationBailleur: {
                TypeBailleur: TypeBailleur.Social,
                isValid: false
              }
            }
          })
        );
        if (this.lessorList.length == 1) {
          this.updateLessor(this.lessorList[0], true);
        }

        break;
      case TypeBailleur.Prive:
        this.private = true;
        this.social = false;

        this.store.dispatch(
          fromService.patchService({
            payload: {
              EnvoiAttestationBailleur: {
                TypeBailleur: TypeBailleur.Prive,
                isValid: false
              }
            }
          })
        );

        this.nextStep();

        break;
      default:
        break;
    }
  }

  async nextStep() {
    let typeBailleur: LessorCertificateEnum;
    if (this.social) typeBailleur = LessorCertificateEnum.Social;
    else typeBailleur = LessorCertificateEnum.Private;

    switch (typeBailleur) {
      case LessorCertificateEnum.Social:
        let lessorName = this.selectedLessor?.value === 'OTR' ? '' : this.selectedLessor?.text;

        this.store.dispatch(
          fromService.patchService({
            payload: {
              EnvoiAttestationBailleur: {
                TypeBailleur: TypeBailleur.Social,
                BailleurSocial: {
                  NomBailleur: lessorName ?? '',
                  Email: this.selectedLessor?.email ?? '',
                  LogementOccupe: null,
                  Reference1: '',
                  Reference2: '',
                  TypeBailleur: this.selectedLessor?.lessorType ?? null
                },
                BailleurPrive: undefined,
                isValid: true
              }
            }
          })
        );
        break;
      case LessorCertificateEnum.Private:
        this.store.dispatch(
          fromService.patchService({
            payload: {
              EnvoiAttestationBailleur: {
                TypeBailleur: TypeBailleur.Prive,
                BailleurSocial: undefined,
                BailleurPrive: {
                  NomBailleur: null,
                  Email: ''
                },
                isValid: true
              }
            }
          })
        );
        break;
    }

    this.updateStepper(false);

    await this.updateAndRedirect(typeBailleur == LessorCertificateEnum.Private);
  }

  async skipStep() {
    let typeBailleur = undefined;
    if (this.social) typeBailleur = TypeBailleur.Social;
    else if (this.private) typeBailleur = TypeBailleur.Prive;

    if (typeBailleur != undefined) {
      if (typeBailleur == TypeBailleur.Social) {
        let lessorName =
          this.selectedLessor?.value === 'OTR' ? '' : this.selectedLessor?.text ?? '';

        this.store.dispatch(
          fromService.patchService({
            payload: {
              EnvAttB: false,
              EnvoiAttestationBailleur: {
                TypeBailleur: typeBailleur,
                BailleurSocial: {
                  NomBailleur: lessorName,
                  Email: this.selectedLessor?.email ?? '',
                  LogementOccupe: null,
                  Reference1: '',
                  Reference2: '',
                  TypeBailleur: this.selectedLessor?.lessorType ?? null
                },
                BailleurPrive: undefined,
                isValid: false
              }
            }
          })
        );
      } else if (typeBailleur == TypeBailleur.Prive) {
        this.store.dispatch(
          fromService.patchService({
            payload: {
              EnvAttB: false,
              EnvoiAttestationBailleur: {
                TypeBailleur: typeBailleur,
                BailleurSocial: undefined,
                BailleurPrive: {
                  NomBailleur: null,
                  Email: ''
                },
                isValid: false
              }
            }
          })
        );
      }
    } else {
      this.store.dispatch(
        fromService.patchService({
          payload: {
            EnvAttB: false,
            EnvoiAttestationBailleur: {
              TypeBailleur: undefined,
              BailleurSocial: undefined,
              BailleurPrive: undefined,
              isValid: false
            }
          }
        })
      );
    }
    this.updateStepper(true);

    await this.updateAndRedirect(typeBailleur == TypeBailleur.Prive);
  }

  async updateAndRedirect(isPrivate: boolean) {
    var lessor = !isPrivate
      ? this.lessorsList.find(p => p.name.includes(this.selectedLessor?.text ?? ''))
      : null;

    const HousingNature = await lastValueFrom(
      this.store.select(fromHousing.selectHousingNature).pipe(take(1))
    );

    this.store.dispatch(
      fromPath.updatePreviousType({
        previousPathType: this.productCode
      })
    );

    if (this.brand == 'VYV') {
      if (HousingNature != NatureResidence.Secondaire) {
        switch (lessor?.lessorType) {
          case 1:
            this.store.dispatch(fromPath.updateType({ pathType: PathType.HOUSING_SOLIDARITY }));
            this.router.navigate([`${ROOT_PATH}${NUMBER_PIECES_PATH}`]);
            break;
          case 3:
          case 4:
            this.store.dispatch(
              fromPath.updateType({ pathType: PathType.HOUSING_BAILLEURS_SOCIAUX })
            );
            this.router.navigate([`${ROOT_PATH}${NUMBER_PIECES_PATH}`]);
            break;
          case 2:
          case 5:
          default:
            var isMetropole = GetMetropoleOrNot(this.zipCode);
            if (isMetropole) this.router.navigate([`${ROOT_PATH}${PREFERENTIAL_RATE_PATH}`]);
            else this.router.navigate([`${ROOT_PATH}${NUMBER_PIECES_PATH}`]);
            break;
        }
      } else {
        this.store.dispatch(fromPath.updateType({ pathType: PathType.HOUSING }));
        this.router.navigate([`${ROOT_PATH}${NUMBER_PIECES_PATH}`]);
      }
    }
    // SI marque autre que VYV alors redirection par défaut
    else {
      if (HousingNature != NatureResidence.Secondaire) {
        var isMetropole = GetMetropoleOrNot(this.zipCode);
        if (isMetropole) this.router.navigate([`${ROOT_PATH}${PREFERENTIAL_RATE_PATH}`]);
        else this.router.navigate([`${ROOT_PATH}${NUMBER_PIECES_PATH}`]);
      } else {
        this.store.dispatch(fromPath.updateType({ pathType: PathType.HOUSING }));
        this.router.navigate([`${ROOT_PATH}${NUMBER_PIECES_PATH}`]);
      }
    }
  }

  async updateStepper(skip: boolean) {
    let service = await lastValueFrom(this.store.select(fromService.selectService).pipe(take(1)));
    let stepper = await lastValueFrom(this.store.select(fromInfo.selectStepper).pipe(take(1)));
    let tmpStepper = JSON.parse(JSON.stringify(stepper)) as Stepper;

    let title = tmpStepper.contract.titles.find(
      (x: { libelle: string }) => x.libelle == this.resource.stepper.contract
    );

    let subtitleS = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.lessorcertificate
    );

    if (subtitleS) {
      if (!skip) {
        subtitleS.isValid = true;
        subtitleS.isActive = true;
      } else {
        subtitleS.isValid = false;
        subtitleS.isActive = false;
        subtitleS.isVisible = false;
      }
    }

    if (service.saisieChoixServices.ResAncAss) {
      let subtitleNS = title?.subtitles.find(
        (x: { url: string }) => x.url == this.resource.stepper.cancellation
      );

      if (subtitleNS) {
        subtitleNS.isActive = true;
      }
    } else {
      let titleN = tmpStepper.contract.titles.find(
        (x: { libelle: string }) => x.libelle == this.resource.stepper.sampling
      );

      if (titleN) titleN.isActive = true;

      let subtitleNS = titleN?.subtitles.find(
        (x: { url: string }) => x.url == this.resource.stepper.payment
      );

      if (subtitleNS) {
        subtitleNS.isActive = true;
      }
    }

    this.store.dispatch(fromInfo.updateStepper({ payload: tmpStepper }));
  }

  onChangeLessor(selectedValue: any) {
    if (selectedValue.value.value != 'OTR') {
      this.updateInfoLessor(selectedValue.value.text);
    }
  }

  updateInfoLessor(lessor: string) {
    switch (lessor) {
      case 'Elogie Siemp':
        this.placeholderRefClient = this.resource.lessorcertificate.elogieSiempRC;
        this.errorRefClient = this.resource.error.elogieSiempRC;
        this.placeholderCC = this.resource.lessorcertificate.elogieSiempCC;
        this.errorCC = this.resource.error.elogieSiempCC;

        this.clientAccountInfo.texts[0] = this.resource.infobulle.elogieSiempCC;
        this.clientAccountInfo.imgPath = this.resource.infobulle.elogieSiempCcPath;
        break;
      case 'Paris Habitat':
        this.placeholderRefClient = this.resource.lessorcertificate.parisHabitatRC;
        this.errorRefClient = this.resource.error.parisHabitatRC;
        this.placeholderCC = this.resource.lessorcertificate.parisHabitatCC;
        this.errorCC = this.resource.error.parisHabitatCC;

        break;
      case 'RIVP':
        this.placeholderRefClient = this.resource.lessorcertificate.rivpRC;
        this.errorRefClient = this.resource.error.rivpRC;
        this.placeholderCC = this.resource.lessorcertificate.rivpCC;
        this.errorCC = this.resource.error.rivpCC;

        this.clientAccountInfo.texts[0] = this.resource.infobulle.rivpCC;
        this.clientAccountInfo.imgPath = this.resource.infobulle.rivpCcPath;
        break;
      default:
        this.placeholderRefClient = this.resource.lessorcertificate.otherRC;
        this.errorRefClient = this.resource.error.otherRC;
        this.placeholderCC = this.resource.lessorcertificate.otherCC;
        this.errorCC = this.resource.error.otherCC;

        break;
    }
  }

  updateLessor(lessor: Lessor, nextStep: boolean = false) {
    if (lessor.value != 'OTR') {
      this.lessor?.setValue(lessor.text);
      this.otherLessor = false;

      this.updateInfoLessor(lessor.text);
    } else {
      this.otherLessor = true;
    }

    this.selectedLessor = lessor;

    console.log('selected lessor : ', this.selectedLessor, lessor);

    if (nextStep || lessor.value == 'OTR') {
      this.nextStep();
    }
  }

  ToLowerCase(event: any) {
    let value = event.target.value;
    event.target.value = value.toLowerCase();
  }

  onNotFoundLessorClick() {
    this.selectedLessor = this.lessorList.find(obj => obj.value === 'OTR');

    this.lessorNotFind = false;
  }

  setClassActive(value: boolean): string {
    return value ? 'active' : 'inactive';
  }
}
